<template>
	<div id="modal-root" v-if="isAuthenticated && !systemLoading">
		<transition name="fade">
			<div id="modal" :class="[{ 'modal-small': small }]">
				<div id="modal-close" v-on:click="closeModal()">
					<b-icon icon="times"></b-icon>
				</div>
				<div id="modal-content-wrapper">
					<b-loading :is-full-page="false" :active.sync="loading" :can-cancel="false"></b-loading>
					<component
						:key="componentKey"
						:is="dynamicModal"
						:id="modelId"
						:data="this.$attrs.data"
						v-on:modal-close="closeModal"
						v-on:modal-resize="resizeModal"
						v-on:settings-updated="handleSettingsUpdate"
					/>
				</div>
			</div>
		</transition>
		<div id="overlay" class="is-visible" v-on:click="closeModal()"></div>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'Modal',
  components: {
    'FormModal': () => import(/* webpackChunkName: "Form" */ '@/components/Modals/Form'),
    'CompanyModal': () => import(/* webpackChunkName: "CompanyModal" */ '@/components/Modals/Company'),
    'UserModal': () => import(/* webpackChunkName: "UserModal" */ '@/components/Modals/User'),
    'AccountModal': () => import(/* webpackChunkName: "AccountModal" */ '@/components/Modals/Account'),
    'SubscriptionModal': () => import(/* webpackChunkName: "SubscriptionModal" */ '@/components/Modals/Subscription'),
    'AccountPropertyModal': () => import(/* webpackChunkName: "AccountPropertyModal" */ '@/components/Modals/AccountProperty'),
    'PropertyImportModal': () => import(/* webpackChunkName: "PropertyImportModal" */ '@/components/Modals/PropertyImport'),
    'AccountFilesModal': () => import(/* webpackChunkName: "AccountFilesModal" */ '@/components/Modals/AccountFile'),
    'SettingsDatatableModal': () => import(/* webpackChunkName: "SettingsDatatableModal" */ '@/components/Modals/SettingsDatatable'),
    'AccountInviteModal': () => import(/* webpackChunkName: "AccountInviteDatatableModal" */ '@/components/Modals/AccountInvite'),
    'RiskControlReport': () => import(/* webpackChunkName: "RiskControlReportModal" */ '@/components/Modals/RiskControlReport'),
    'RoleModal': () => import(/* webpackChunkName: "RoleModal" */ '@/components/Modals/Role')
  },
  data () {
    return {
      loading: true,
      small: this.$attrs.small,
      warn: false,
      componentKey: 0
    }
  },
  props: {
    performsNavigation: {
      type: Boolean,
      default: true
    }
  },
  created () {
    this.beforeOpen()

    this.$root.$on('modal-loading', status => {
      this.loading = status
    })

    this.$root.$on('modal-warn', status => {
      this.warn = status
    })
  },
  beforeDestroy () {
    this.beforeClose()
  },
  mounted () {
    if (this.performsNavigation) {
      this.$store.commit('setUnderModalLink', this.$route.matched[0])
    }
  },
  watch: {
    modelId: function () {
      this.componentKey++
    },
    loading: function (newVal, oldVal) {
      if (oldVal === true) {
        this.dropListeners()
      }
    },
    dynamicModal: function (newVal, oldVal) {
      this.small = this.$attrs.small
    }
  },
  computed: {
    ...mapState({
      systemLoading: state => state.loading
    }),
    ...mapGetters({
      isAuthenticated: 'isAuthenticated'
    }),
    dynamicModal () {
      if (this.$attrs.modalType === 'form') {
        return 'FormModal'
      } else if (this.$attrs.modalType === 'company') {
        return 'CompanyModal'
      } else if (this.$attrs.modalType === 'accounts') {
        return 'AccountModal'
      } else if (this.$attrs.modalType === 'users') {
        return 'UserModal'
      } else if (this.$attrs.modalType === 'subscriptions') {
        return 'SubscriptionModal'
      } else if (this.$attrs.modalType === 'account-properties') {
        return 'AccountPropertyModal'
      } else if (this.$attrs.modalType === 'property-import') {
        return 'PropertyImportModal'
      } else if (this.$attrs.modalType === 'account-files') {
        return 'AccountFilesModal'
      } else if (this.$attrs.modalType === 'settings') {
        return 'SettingsDatatableModal'
      } else if (this.$attrs.modalType === 'account-invite') {
        return 'AccountInviteModal'
      } else if (this.$attrs.modalType === 'risk-control-report') {
        return 'RiskControlReport'
      } else if (this.$attrs.modalType === 'roles') {
        return 'RoleModal'
      }

      return null
    },
    modelId () {
      if (this.$route.path.includes('create')) {
        return undefined
      }

      let id = this.$route.params

      if (this.$route.params) {
        for (let param in this.$route.params) {
          if (!this.$route.params.hasOwnProperty(param)) continue
          if (param.endsWith('id')) {
            id = this.$route.params[param]
          }
        }
      }

      return id
    }
  },
  methods: {
    // Check if user is currently inside a field and pressing escape
    checkIfActiveField () {
      let activeElement = document.activeElement
      let inputs = ['input', 'select', 'textarea']

      return !!(activeElement && inputs.indexOf(activeElement.tagName.toLowerCase()) !== -1)
    },
    closeModal () {
      this.beforeClose()
      if (this.warn === true) {
        this.$buefy.dialog.confirm({
          title: 'Are you sure?',
          message: 'It looks like you may not be done with that yet, are you sure you want to close this window and forget all about it?',
          confirmText: 'Close Sidebar',
          cancelText: 'No, let me finish',
          type: 'is-warning',
          hasIcon: true,
          onConfirm: () => {
            this.warn = false
            this.closeModal()
          },
          onCancel: () => {
            this.beforeOpen()
          }
        })
      } else if (!this.performsNavigation) {
        this.$emit('modal-closed')
      } else {
        this.$router.push(this.$store.state.underModalLink || `/${this.$route.path.split('/')[1]}`)
        this.$store.commit('setUnderModalLink', null)
      }
    },
    resizeModal (bool) {
      this.small = bool
    },
    beforeOpen () {
      document.addEventListener('keyup', this.closeModalViaEscape)
    },
    beforeClose () {
      document.removeEventListener('keyup', this.closeModalViaEscape)
    },
    closeModalViaEscape (e) {
      e.stopImmediatePropagation()
      if (e.keyCode !== 27) return

      // if user is in a field, don't close the modal
      // but also remove the focus so they can just hit exc again
      if (this.checkIfActiveField()) {
        document.activeElement.blur()
        return
      }

      this.closeModal()
    },
    dropAllow (event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    dropHandle (event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'

      const files = event.dataTransfer.files || event.target.files
      if (files.length) {
        this.$awn.warning('Oops! Drag those files into the upload space on the right.')
      }
    },
    dropListeners () {
      const overlay = document.getElementById('overlay')
      const modal = document.getElementById('modal')

      overlay.addEventListener('drop', this.dropHandle)
      overlay.addEventListener('dragover', this.dropAllow)
      overlay.addEventListener('dragenter', this.dropAllow)

      modal.addEventListener('drop', this.dropHandle)
      modal.addEventListener('dragover', this.dropAllow)
      modal.addEventListener('dragenter', this.dropAllow)
    },
    handleSettingsUpdate () {
      this.$emit('settings-updated')
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Elements/Modal.vue